import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  HStack,
  Spinner,
  Text,
  VStack,
  useToast,
  IconButton,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
} from '@chakra-ui/react';
import { FiRefreshCw, FiMenu } from 'react-icons/fi';
import SaktiFilterBar from '../components/SaktiFilterBar';
import SaktiKpiCards from '../components/SaktiKpiCards';
import SaktiBarChartCard from '../components/SaktiBarChartCard';
import SaktiRecentActivity from '../components/SaktiRecentActivity';
import SaktiSidebar from '../components/SaktiSidebar';
import { useSaktiLiveDashboard } from '../hooks/useSaktiLiveDashboard';
import SaktiApiService from '../services/saktiApiService';
import { SaktiFilterOptions, SaktiFilters } from '../types';
import { SAKTI_UI_SIZE } from '../uiSizes';

const getTodayForApi = () => {
  return new Date().toISOString().split('T')[0];
};

const getConnectionBadgeColor = (status: string) => {
  if (status === 'connected') return 'green';
  if (status === 'reconnecting') return 'orange';
  if (status === 'error') return 'red';
  return 'yellow';
};

const INITIAL_OPTIONS: SaktiFilterOptions = {
  companies: [],
  estates: [],
  divisions: [],
};

const SaktiPage: React.FC = () => {
  const toast = useToast();
  const [options, setOptions] = useState<SaktiFilterOptions>(INITIAL_OPTIONS);
  const [filters, setFilters] = useState<SaktiFilters>({ date: getTodayForApi() });
  const [draftFilters, setDraftFilters] = useState<SaktiFilters>({ date: getTodayForApi() });
  const [loadingOptions, setLoadingOptions] = useState<boolean>(true);

  const { 
    isOpen: isMobileSidebarOpen, 
    onOpen: onMobileSidebarOpen, 
    onClose: onMobileSidebarClose 
  } = useDisclosure();

  const { dashboard, loading, error, connectionStatus, lastUpdatedAt, reconnect } =
    useSaktiLiveDashboard(filters);

  const loadFilterOptions = useCallback(async () => {
    setLoadingOptions(true);
    try {
      const fetchedOptions = await SaktiApiService.fetchFilterOptions();
      setOptions(fetchedOptions);

      if (fetchedOptions.companies.length > 0) {
        const defaultCompanyId = fetchedOptions.companies[0].id;
        const defaultFilters = {
          date: getTodayForApi(),
          company_id: defaultCompanyId,
          estate_id: undefined,
          division_id: undefined,
        };

        setFilters(defaultFilters);
        setDraftFilters(defaultFilters);
      }
    } catch (optionsError) {
      toast({
        title: 'Gagal memuat opsi filter',
        description:
          optionsError instanceof Error
            ? optionsError.message
            : 'Terjadi kesalahan saat memuat data opsi filter',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setLoadingOptions(false);
    }
  }, [toast]);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  const onApplyFilters = useCallback(() => {
    setFilters({
      ...draftFilters,
      date: getTodayForApi(),
    });
  }, [draftFilters]);

  const onManualReconnect = useCallback(() => {
    reconnect();
    toast({
      title: 'Menyambungkan ulang stream',
      status: 'info',
      duration: 2500,
      isClosable: true,
      position: 'top-right',
    });
  }, [reconnect, toast]);

  const statusLabel = useMemo(() => {
    if (connectionStatus === 'connected') return 'Connected';
    if (connectionStatus === 'reconnecting') return 'Reconnecting';
    if (connectionStatus === 'error') return 'Disconnected';
    return 'Connecting';
  }, [connectionStatus]);

  return (
    <Box minH="100vh" bg="#ECEEF1">
      <Flex align="stretch">
        {/* Desktop Sidebar */}
        <Box display={{ base: 'none', xl: 'block' }}>
          <SaktiSidebar activeMenu="home" />
        </Box>

        {/* Mobile Sidebar Drawer */}
        <Drawer
          isOpen={isMobileSidebarOpen}
          placement="left"
          onClose={onMobileSidebarClose}
          size="xs"
        >
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton zIndex={10} color="gray.500" />
            <DrawerBody p={0}>
              <SaktiSidebar activeMenu="home" />
            </DrawerBody>
          </DrawerContent>
        </Drawer>

        <Box flex={1} p={SAKTI_UI_SIZE.pagePadding}>
          <VStack align="stretch" spacing={SAKTI_UI_SIZE.sectionGap}>
            <HStack justify="space-between" align={{ base: 'start', md: 'center' }} flexWrap="wrap">
              <HStack spacing={3} align="center">
                <IconButton
                  aria-label="Open menu"
                  icon={<FiMenu />}
                  variant="outline"
                  bg="white"
                  display={{ base: 'flex', xl: 'none' }}
                  onClick={onMobileSidebarOpen}
                />
                <VStack align="start" spacing={0}>
                  <Text fontSize={SAKTI_UI_SIZE.pageTitle} fontWeight="bold" color="gray.900" lineHeight="shorter">
                    Dashboard Admin Panel SAKTI
                  </Text>
                  {dashboard?.companyName && (
                    <Text color="gray.600" fontWeight="medium" fontSize={SAKTI_UI_SIZE.pageSubtitle}>
                      {dashboard.companyName}
                    </Text>
                  )}
                </VStack>
              </HStack>

              <HStack spacing={3} mt={{ base: 2, md: 0 }}>
                <Badge colorScheme={getConnectionBadgeColor(connectionStatus)} px={3} py={1} borderRadius="full">
                  {statusLabel}
                </Badge>
                <Button
                  size="sm"
                  leftIcon={<FiRefreshCw />}
                  onClick={onManualReconnect}
                  variant="outline"
                  bg="white"
                >
                  Refresh Stream
                </Button>
              </HStack>
            </HStack>

            {error && (
              <Alert status="warning" borderRadius="lg">
                <AlertIcon />
                <AlertTitle fontSize="sm">{error}</AlertTitle>
              </Alert>
            )}

            {lastUpdatedAt && (
              <Text color="gray.500" fontSize="xs">
                Last updated: {new Date(lastUpdatedAt).toLocaleString('id-ID')}
              </Text>
            )}

            <SaktiFilterBar
              filters={draftFilters}
              options={options}
              onFiltersChange={setDraftFilters}
              onApply={onApplyFilters}
              isApplying={connectionStatus === 'connecting' || connectionStatus === 'reconnecting'}
            />

            <SaktiKpiCards loading={loading || loadingOptions} totals={dashboard?.kpi ?? {
              total_bcc_cnt: 0,
              total_spb_cnt: 0,
              total_bcc_weight: 0,
              total_restan_weight: 0,
            }} />

            {(loading || loadingOptions) && !dashboard ? (
              <Box bg="white" borderRadius="xl" p={8}>
                <HStack justify="center" spacing={3}>
                  <Spinner color="customGreen.500" />
                  <Text>Memuat live dashboard Sakti...</Text>
                </HStack>
              </Box>
            ) : (
              <Grid templateColumns={{ base: '1fr', xl: '3fr 1.1fr' }} gap={5}>
                <VStack align="stretch" spacing={5}>
                  <SaktiBarChartCard
                    title="Realisasi Taksasi"
                    subtitle="Realisasi Taksasi Tiap Estate"
                    data={dashboard?.realisasiTaksasi ?? []}
                    loading={loading || loadingOptions}
                  />
                  <SaktiBarChartCard
                    title="Realisasi Kehadiran Pemanen (HK)"
                    subtitle="Realisasi HK Tiap Estate"
                    data={dashboard?.realisasiHk ?? []}
                    loading={loading || loadingOptions}
                  />
                </VStack>

                <SaktiRecentActivity
                  loading={loading || loadingOptions}
                  activities={dashboard?.recentActivities ?? []}
                />
              </Grid>
            )}
          </VStack>
        </Box>
      </Flex>
    </Box>
  );
};

export default SaktiPage;
